// src/pages/Verification.tsx
// Plezyr Admin - Profile Verification Management
import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ImageList,
  ImageListItem,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PhotoIcon from '@mui/icons-material/Photo';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';

interface VerificationUser {
  id: number;
  name: string;
  email: string | null;
  avatar_url: string | null;
  created_at: string | null;
}

interface PendingVerification {
  id: number;
  user: VerificationUser;
  has_photos: boolean;
  photos_count: number;
  has_bio: boolean;
  bio_preview: string | null;
  is_verified: boolean;
  submitted_at: string;
}

interface VerificationsResponse {
  success: boolean;
  verifications: PendingVerification[];
  total: number;
  skip: number;
  limit: number;
}

interface VerificationDetailResponse {
  success: boolean;
  profile: {
    id: number;
    user_id: number;
    user_name: string;
    user_email: string | null;
    gender: string | null;
    sexuality: string | null;
    looking_for: string | null;
    bio: string | null;
    photos: string[];
    interests: string[];
    job_title: string | null;
    company: string | null;
    school: string | null;
    is_verified: boolean;
    verified_at: string | null;
    created_at: string | null;
    last_active: string | null;
  };
}

interface VerificationActionResponse {
  success: boolean;
  message: string;
  user_id: number;
}

// Helper to extract error message from API response
const extractErrorMessage = (err: any): string => {
  const detail = err.response?.data?.detail;
  if (!detail) return 'Une erreur est survenue';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors
    return detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return JSON.stringify(detail);
};

const Verification: React.FC = () => {
  const { t } = useTranslation();
  const { success, error: showError } = useNotification();

  const [verifications, setVerifications] = useState<PendingVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('pending');

  // Dialog state
  const [selectedVerification, setSelectedVerification] = useState<PendingVerification | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<VerificationDetailResponse['profile'] | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'view' | 'reject'>('view');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    verified: 0,
  });

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = { limit: 100 };
      if (statusFilter !== 'all') {
        params.status_filter = statusFilter;
      }

      const response = await api.get<VerificationsResponse>('/admin/verifications', { params });
      setVerifications(response.data.verifications);

      // Calculate stats
      const allResponse = await api.get<VerificationsResponse>('/admin/verifications', {
        params: { limit: 1000 }
      });
      const allVerifications = allResponse.data.verifications;
      setStats({
        total: allVerifications.length,
        pending: allVerifications.filter(v => !v.is_verified).length,
        verified: allVerifications.filter(v => v.is_verified).length,
      });
    } catch (err: any) {
      console.error('Failed to fetch verifications:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVerifications();
  }, [statusFilter]);

  const fetchDetail = async (profileId: number) => {
    try {
      setDetailLoading(true);
      const response = await api.get<VerificationDetailResponse>(`/admin/verifications/${profileId}`);
      setSelectedDetail(response.data.profile);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async (profileId: number) => {
    try {
      setActionLoading(true);
      await api.post<VerificationActionResponse>(`/admin/verifications/${profileId}/approve`);
      success('Profil vérifié avec succès');
      fetchVerifications();
      setDialogOpen(false);
      setSelectedDetail(null);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (profileId: number) => {
    try {
      setActionLoading(true);
      await api.post<VerificationActionResponse>(
        `/admin/verifications/${profileId}/reject`,
        null,
        { params: { reason: rejectReason || 'Non conforme aux conditions' } }
      );
      success('Profil rejeté');
      setRejectReason('');
      fetchVerifications();
      setDialogOpen(false);
      setSelectedDetail(null);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const openViewDialog = (verification: PendingVerification) => {
    setSelectedVerification(verification);
    setDialogType('view');
    setDialogOpen(true);
    fetchDetail(verification.id);
  };

  const openRejectDialog = (verification: PendingVerification) => {
    setSelectedVerification(verification);
    setDialogType('reject');
    setRejectReason('');
    setDialogOpen(true);
  };

  const statusChip = (isVerified: boolean) => {
    if (isVerified) {
      return <Chip label="Vérifié" color="success" size="small" icon={<VerifiedUserIcon />} />;
    }
    return <Chip label="En attente" color="warning" size="small" />;
  };

  const filteredVerifications = verifications.filter((v) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        v.user.name.toLowerCase().includes(search) ||
        (v.user.email && v.user.email.toLowerCase().includes(search))
      );
    }
    return true;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} color="#1e293b">
            {t('verification.title', 'Vérification des profils')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('verification.subtitle', 'Vérifiez et validez les profils des utilisateurs')}
          </Typography>
        </Box>
        <Tooltip title="Rafraîchir">
          <IconButton onClick={fetchVerifications}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={4}>
          <Card sx={{ bgcolor: '#f8fafc' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700}>{stats.total}</Typography>
              <Typography variant="caption" color="text.secondary">Total profils</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={4}>
          <Card sx={{ bgcolor: '#fef3c7' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">{stats.pending}</Typography>
              <Typography variant="caption" color="text.secondary">En attente</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={4}>
          <Card sx={{ bgcolor: '#d1fae5' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="success.main">{stats.verified}</Typography>
              <Typography variant="caption" color="text.secondary">Vérifiés</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          placeholder="Rechercher un utilisateur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            value={statusFilter}
            label="Statut"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="verified">Vérifiés</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Verifications Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600 }}>Utilisateur</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Photos</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Bio</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Soumis le</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVerifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Aucun profil à vérifier
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredVerifications.map((verification) => (
                <TableRow key={verification.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        src={verification.user.avatar_url || undefined}
                        sx={{ width: 40, height: 40, bgcolor: '#9333EA' }}
                      >
                        {verification.user.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {verification.user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {verification.user.id}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {verification.user.email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<PhotoIcon />}
                      label={verification.photos_count}
                      size="small"
                      variant="outlined"
                      color={verification.has_photos ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {verification.has_bio ? (
                      <Tooltip title={verification.bio_preview || ''}>
                        <Chip label="Oui" size="small" color="success" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Chip label="Non" size="small" color="default" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {verification.submitted_at
                        ? new Date(verification.submitted_at).toLocaleDateString('fr-FR')
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{statusChip(verification.is_verified)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Voir détails">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openViewDialog(verification)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!verification.is_verified && (
                      <>
                        <Tooltip title="Approuver">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(verification.id)}
                            disabled={actionLoading}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Rejeter">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openRejectDialog(verification)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View/Reject Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedDetail(null);
        }}
        maxWidth="md"
        fullWidth
      >
        {selectedVerification && (
          <>
            <DialogTitle>
              {dialogType === 'reject'
                ? `Rejeter le profil de ${selectedVerification.user.name} ?`
                : `Profil de ${selectedVerification.user.name}`}
            </DialogTitle>
            <DialogContent>
              {dialogType === 'view' ? (
                detailLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : selectedDetail ? (
                  <Stack spacing={3} sx={{ mt: 1 }}>
                    {/* User Info */}
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        src={selectedDetail.photos?.[0] || undefined}
                        sx={{ width: 64, height: 64, bgcolor: '#9333EA' }}
                      >
                        {selectedDetail.user_name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{selectedDetail.user_name}</Typography>
                        <Typography color="text.secondary">
                          {selectedDetail.user_email || 'Pas d\'email'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {selectedDetail.user_id}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Profile Info */}
                    <Stack direction="row" spacing={4}>
                      {selectedDetail.gender && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Genre</Typography>
                          <Typography>{selectedDetail.gender}</Typography>
                        </Box>
                      )}
                      {selectedDetail.sexuality && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Sexualité</Typography>
                          <Typography>{selectedDetail.sexuality}</Typography>
                        </Box>
                      )}
                      {selectedDetail.looking_for && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Recherche</Typography>
                          <Typography>{selectedDetail.looking_for}</Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Bio */}
                    {selectedDetail.bio && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Bio
                        </Typography>
                        <Typography>{selectedDetail.bio}</Typography>
                      </Box>
                    )}

                    {/* Interests */}
                    {selectedDetail.interests && selectedDetail.interests.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Intérêts
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {selectedDetail.interests.map((interest, index) => (
                            <Chip key={index} label={interest} size="small" />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {/* Photos */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Photos ({selectedDetail.photos?.length || 0})
                      </Typography>
                      {selectedDetail.photos && selectedDetail.photos.length > 0 ? (
                        <ImageList cols={3} gap={8}>
                          {selectedDetail.photos.map((photo, index) => (
                            <ImageListItem key={index}>
                              <img
                                src={photo}
                                alt={`Photo ${index + 1}`}
                                loading="lazy"
                                style={{ borderRadius: 8, maxHeight: 200, objectFit: 'cover' }}
                              />
                            </ImageListItem>
                          ))}
                        </ImageList>
                      ) : (
                        <Typography color="text.secondary" variant="body2">
                          Aucune photo
                        </Typography>
                      )}
                    </Box>

                    {/* Work/Education */}
                    {(selectedDetail.job_title || selectedDetail.company || selectedDetail.school) && (
                      <Stack direction="row" spacing={4}>
                        {selectedDetail.job_title && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Métier</Typography>
                            <Typography>{selectedDetail.job_title}</Typography>
                          </Box>
                        )}
                        {selectedDetail.company && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Entreprise</Typography>
                            <Typography>{selectedDetail.company}</Typography>
                          </Box>
                        )}
                        {selectedDetail.school && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">École</Typography>
                            <Typography>{selectedDetail.school}</Typography>
                          </Box>
                        )}
                      </Stack>
                    )}

                    {/* Dates */}
                    <Stack direction="row" spacing={4}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Profil créé le
                        </Typography>
                        <Typography>
                          {selectedDetail.created_at
                            ? new Date(selectedDetail.created_at).toLocaleString('fr-FR')
                            : '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Dernière activité
                        </Typography>
                        <Typography>
                          {selectedDetail.last_active
                            ? new Date(selectedDetail.last_active).toLocaleString('fr-FR')
                            : '-'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                ) : (
                  <Typography color="text.secondary">
                    Aucun détail disponible
                  </Typography>
                )
              ) : (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Typography>
                    Vous êtes sur le point de rejeter la vérification de ce profil.
                  </Typography>
                  <TextField
                    label="Raison du rejet"
                    multiline
                    rows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Expliquez pourquoi le profil est rejeté..."
                    fullWidth
                  />
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setDialogOpen(false);
                setSelectedDetail(null);
              }}>
                Fermer
              </Button>
              {dialogType === 'view' && !selectedVerification.is_verified && (
                <>
                  <Button
                    color="error"
                    onClick={() => {
                      setDialogType('reject');
                    }}
                  >
                    Rejeter
                  </Button>
                  <Button
                    color="success"
                    variant="contained"
                    onClick={() => handleApprove(selectedVerification.id)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? <CircularProgress size={20} /> : 'Approuver'}
                  </Button>
                </>
              )}
              {dialogType === 'reject' && (
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => handleReject(selectedVerification.id)}
                  disabled={actionLoading}
                >
                  {actionLoading ? <CircularProgress size={20} /> : 'Confirmer le rejet'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

// Grid component for stats
const Grid = ({ container, item, children, ...props }: any) => {
  if (container) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: -1, ...props.sx }}>
        {children}
      </Box>
    );
  }
  const { xs, sm, md } = props;
  const getWidth = () => {
    const cols = md || sm || xs || 12;
    return `${(cols / 12) * 100}%`;
  };
  return (
    <Box sx={{ width: getWidth(), padding: 1 }}>
      {children}
    </Box>
  );
};

export default Verification;
